/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/CompactLaneChips.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-COMPACTCHIPS-001
 * PURPOSE: Collapse the four metadata lanes into one row of chips at compact
 *   width, each opening its OWN lane in an anchored overlay.
 * ROLE IN ARCHITECTURE: Compact-tier presentation of ExpandedContext's grid.
 *   It renders no controls of its own — the overlay body is the very same
 *   lane element the wide tier puts in the grid.
 * MAIN EXPORTS: CompactLaneChips.
 * NON-RESPONSIBILITIES: No lane content, no handlers, no menu routing, no
 *   overlay geometry (AdaptiveOverlay owns portal/clamp/flip/dismiss).
 *
 * WHY: The compact tier stacked all four lanes, so a phone-width reader spent
 *   four rows on metadata before the message started. Shrinking the lanes was
 *   not available — they are already at the touch floor — so the answer is to
 *   show one line of summaries and let the operator open the group they want.
 *
 * WHY THE OVERLAY HOSTS THE LANE, NOT A MENU: the obvious cheap version is to
 *   point each chip at the existing LaneContextMenu. That silently removes
 *   function. The lane menus are ACTION lists, and the registry documents that
 *   create_personal_label / create_team_label resolve to null "because they
 *   live inside the CategoryChip picker" — so a menu-only Class chip would
 *   leave a narrow reader unable to change an email's classification at all.
 *   Mounting the real lane keeps every control, and keeps exactly one
 *   implementation of each.
 *
 * FLOW:
 * ┌───────────────┐   ┌──────────────┐   ┌──────────────────────────┐
 * │ lane summaries│ → │ chip pressed │ → │ AdaptiveOverlay(lane node)│
 * └───────────────┘   └──────────────┘   └──────────────────────────┘
 */
import { type ReactNode, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { AdaptiveOverlay } from '@quotech/ui'
import { cn } from '../../../_internal/lib/utils'
import type { ReaderTriageLaneId } from '../menus/menuScopes'
import type { LaneChipSummary } from './laneSummary'

/** Popover width at compact tier. Below 640 AdaptiveOverlay uses a sheet. */
const LANE_POPOVER_WIDTH = 320

export interface CompactLaneChipsProps {
  summaries: readonly LaneChipSummary[]
  /** The SAME lane elements the wide grid renders, keyed by lane. */
  laneNodes: Readonly<Record<ReaderTriageLaneId, ReactNode>>
  /**
   * True while a layer the lane itself opened (its ellipsis menu, the company
   * drawer, the signals manager, the placement manager) is on screen ABOVE
   * this popover. See LaneChip for why that has to suppress dismissal.
   */
  laneOverlayOpen?: boolean
}

interface LaneChipProps {
  summary: LaneChipSummary
  laneOverlayOpen: boolean
  children: ReactNode
}

/**
 * One collapsed group: a chip plus the overlay holding its live lane.
 *
 * Inputs: the group's summary and its lane element.
 * Output: a button that toggles an anchored overlay; the lane inside keeps
 * its own handlers, so nothing is re-wired here.
 * Failure mode: a group with no value still renders an openable chip — an
 * unfiled email is exactly when the filing controls are wanted.
 */
function LaneChip({ summary, laneOverlayOpen, children }: LaneChipProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  /**
   * Ignore self-dismissal while the lane has a layer of its own open.
   *
   * AdaptiveOverlay closes on any mousedown outside its panel, and the lane's
   * ellipsis menu portals to document.body — so pressing an item in that menu
   * counts as "outside" and would close the popover the menu is anchored
   * INSIDE. The operator would watch the panel vanish mid-press, and the menu
   * would be left floating over the page pointing at a button that no longer
   * exists. Holding the popover open while a child layer is up keeps the
   * anchor alive and returns the operator to the lane when the child closes.
   *
   * Only self-dismissal is suppressed. Pressing the chip still toggles the
   * popover shut, and once the child layer closes the next outside press
   * dismisses normally.
   */
  const requestOpenChange = (next: boolean) => {
    if (!next && laneOverlayOpen) return
    setOpen(next)
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        data-testid={`lane-chip-${summary.lane}`}
        data-lane-chip-open={open ? 'true' : 'false'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        // min-h-8 / coarse 44: these are the only handle a narrow reader has
        // on this metadata, so they sit at the touch floor, not the chip
        // floor. shrink-0 keeps the row scrollable rather than squeezing four
        // chips into unreadable slivers.
        className={cn(
          'inline-flex min-h-8 shrink-0 items-center gap-1 rounded-full border border-border',
          'bg-muted/40 px-2.5 text-[11px] leading-none text-foreground',
          'hover:bg-accent focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-primary/50 focus-visible:ring-offset-1',
          'pointer-coarse:min-h-11',
          open && 'bg-accent',
        )}
      >
        <span className="shrink-0 font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {summary.label}
        </span>
        {summary.value && (
          // max-w caps ONE long value from pushing the other three chips off
          // the row; the full text is in the overlay a tap away.
          <span className="max-w-[9rem] truncate">{summary.value}</span>
        )}
        {summary.extra > 0 && (
          <span className="shrink-0 rounded-full bg-primary/10 px-1 text-[10px] font-medium text-primary">
            +{summary.extra}
          </span>
        )}
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 text-muted-foreground', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      <AdaptiveOverlay
        open={open}
        onOpenChange={requestOpenChange}
        anchorRef={anchorRef}
        label={`${summary.label} details`}
        testId={`lane-chip-panel-${summary.lane}`}
        popoverWidth={LANE_POPOVER_WIDTH}
      >
        {children}
      </AdaptiveOverlay>
    </>
  )
}

/**
 * Render the four collapsed metadata groups on one line.
 *
 * Inputs: the four summaries (already ordered) and their lane elements.
 * Output: a horizontally scrollable, never-wrapping chip row. Scrolling is
 * the deliberate overflow answer here: wrapping would put the compact tier
 * back to a variable-height block, which is what collapsing set out to fix.
 * Failure mode: none — four chips always render, empty groups included.
 */
export function CompactLaneChips({
  summaries,
  laneNodes,
  laneOverlayOpen = false,
}: CompactLaneChipsProps) {
  return (
    <div
      data-testid="reader-compact-lane-chips"
      className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto px-0.5 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {summaries.map((summary) => (
        <LaneChip
          key={summary.lane}
          summary={summary}
          laneOverlayOpen={laneOverlayOpen}
        >
          {laneNodes[summary.lane]}
        </LaneChip>
      ))}
    </div>
  )
}
